import { getAuthenticatedUser } from "./auth";

export interface ErrorLogPayload {
  message: string;
  stack?: string;
  href: string;
  userAgent: string;
  user?: string | null;
  timestamp: string;
  context?: Record<string, any>;
}

/**
 * Tracks an application error by logging it locally and submitting it to the remote backend service.
 */
export async function trackError(
  error: Error,
  context?: Record<string, any>,
): Promise<void> {
  const currentUser = getAuthenticatedUser();
  const userIdentifier = currentUser ? `${currentUser.fullName} (${currentUser.id})` : null;

  const payload: ErrorLogPayload = {
    message: error.message || "Unknown error",
    stack: error.stack,
    href: typeof window !== "undefined" ? window.location.href : "SSR",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "SSR",
    user: userIdentifier,
    timestamp: new Date().toISOString(),
    context,
  };

  console.error("[ErrorTracking] Captured application error:", payload);

  // Send asynchronously to remote backend monitoring service
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    await fetch("https://gealuce.no-gravity.dev/integrations/b2b/logs/error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    console.info("[ErrorTracking] Remote logging completed successfully.");
  } catch (err) {
    // Fail silently so logging never interrupts user flow
    console.warn(
      "[ErrorTracking] Remote logging failed (or endpoint not implemented):",
      err,
    );
  }
}

/**
 * Submits a manual support ticket with description and original error context.
 */
export async function submitSupportTicket(
  description: string,
  errorDetails: string | null,
): Promise<string> {
  const ticketId = `TICKET-${Math.floor(100000 + Math.random() * 900000)}`;

  const currentUser = getAuthenticatedUser();
  const userIdentifier = currentUser ? `${currentUser.fullName} (${currentUser.id})` : null;

  const payload = {
    ticketId,
    description,
    errorDetails,
    href: typeof window !== "undefined" ? window.location.href : "",
    user: userIdentifier,
    timestamp: new Date().toISOString(),
  };

  console.info("[ErrorTracking] Submitting support ticket:", payload);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);

    await fetch("https://gealuce.no-gravity.dev/integrations/b2b/support/ticket", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
  } catch (err) {
    console.warn(
      "[ErrorTracking] Remote support ticket delivery failed (mocked response fallback):",
      err,
    );
  }

  return ticketId;
}
